import * as Sentry from '@sentry/react';

import { AD_UPDATE_CHECK_DELAY_MS } from './constants';

/**
 * Check if a container DOM element is an iframe element, or it has iframe element
 *
 * @param element - <HTMLElement>(required)
 * @param tagName - string(optional)
 *
 * @return boolean
 */
export const hasAdElement = (element, tagName) => {
    if (element.tagName === tagName) {
        // If the element is the same as tagName, return true
        return true;
    } else {
        // Otherwise, check each child element recursively
        for (let i = 0; i < element.childElementCount; i++) {
            if (hasAdElement(element.children[i], tagName)) {
                // If any child element has the same element as tagName, return true
                return true;
            }
        }
    }
    // If no element the same as tagName is found, return false
    return false;
};

export const checkAdContainerAndSendAnalytics = (
    bannerElement,
    gaEventName,
    gameGA,
    ga,
    tagName,
    delay = AD_UPDATE_CHECK_DELAY_MS
) => {
    setTimeout(() => {
        const hasAd = hasAdElement(bannerElement, tagName);

        if (hasAd) {
            gameGA?.sendDesignEvent(`Ad:${gaEventName}`);
            ga?.basicGaEvent('ad', `${gaEventName}`);
        } else {
            gameGA?.sendErrorEvent('Warning', `Banner update failed: ${gaEventName}`);
            ga?.gaEvent('banner_call_fail', {
                'category': 'ad',
                'banner-name': gaEventName,
            });
        }
    }, delay);
};

/**
 * Builds an Epom request URL with shared LP tracking query params.
 * Used by banner fetch and rewarded VAST URL construction.
 *
 * @param {string} baseUrl - Endpoint or token URL (without query string).
 * @param {{ hash?: string, refid1?: string, refid2?: string, refid3?: string, source?: string }} [adTracking]
 * @param {Record<string, string|number>} [extraParams] - Extra query params prepended before tracking (e.g. `{ key: bannerId }`).
 * @returns {string}
 */
export function buildEpomRequestUrl(baseUrl, adTracking = {}, extraParams = {}) {
    const { hash, refid1, refid2, refid3, source } = adTracking;
    const lpSource =
        String(source || '')
            .toLowerCase()
            .replace(/\s+/g, '-') || 'non';

    const extraQuery = Object.entries(extraParams)
        .map(([key, value]) => `${key}=${value}`)
        .join('&');
    const trackingQuery = `cp.hash=${hash || 0}&cp.refid1=${refid1 || 0}&cp.refid2=${refid2 || 0}&cp.refid3=${refid3 || 0}&cp.source=${lpSource}`;
    const query = extraQuery ? `${extraQuery}&${trackingQuery}` : trackingQuery;

    return `${baseUrl}?${query}`;
}

/**
 * Fetches an Epom banner script and injects it into the corresponding <ins> element.
 * Builds a request URL with the provided banner key and optional tracking params
 * (hash, refid1, refid3). If any of these params are not provided, `0` is used as a fallback value.
 *
 * @async
 * @function fetchEpomBanner
 * @param {Object} params - Configuration object.
 * @param {string} params.fetchUrl - Base URL of the Epom endpoint.
 * @param {string} params.bannerId - Identifier used as the `key` query parameter and to locate the <ins> element.
 * @param {string} [params.refid1] - Optional tracking parameter sent as `cp.refid1` (defaults to `0` if falsy).
 * @param {string} [params.refid2] - Optional tracking parameter sent as `cp.refid1` (defaults to `0` if falsy).
 * @param {string} [params.refid3] - Optional tracking parameter sent as `cp.refid3` (defaults to `0` if falsy).
 * @param {string} [params.hash] - Optional tracking parameter sent as `cp.hash` (defaults to `0` if falsy).
 * @param {string} [params.source] - Landing source name string.
 * @returns {Promise<void>} Resolves when the banner content has been fetched and processed.
 */
export async function fetchEpomBanner({
    fetchUrl,
    bannerId,
    hash,
    refid1,
    refid2,
    refid3,
    source,
}) {
    const url = buildEpomRequestUrl(
        fetchUrl,
        { hash, refid1, refid2, refid3, source },
        { key: bannerId }
    );

    const response = await fetch(url);
    const adScript = await response.text();

    const bannerElement = document.querySelector(`ins[data-key="${bannerId}"]`);

    if (bannerElement) {
        bannerElement.innerHTML = adScript; // Insert the HTML content into the <ins> element

        const scripts = bannerElement.querySelectorAll('script');

        scripts.forEach((script) => {
            const newScript = document.createElement('script');

            if (script.src) {
                newScript.src = script.src;
            } else {
                newScript.textContent = script.textContent;
            }

            document.body.appendChild(newScript);
            document.body.removeChild(newScript);
        });
    }
}

const PREFIX_MAP = [
    ['ad:banner:', 'banners'],
    ['ad:side:', 'sideBanners'],
    ['ad:reward:', 'rewards'],
];

/**
 * Whether an offer ad value represents "no ad" and should be ignored.
 * Matches a bare `"empty"` or a single `provider:empty` pair (e.g. `"epom:empty"`).
 *
 * @param {string} value - Trimmed offer value.
 * @returns {boolean}
 */
const isEmptyAdValue = (value) => value === 'empty' || /^[^,:]+:empty$/i.test(value);

/**
 * Derives the unique ad-provider slugs used by a grouped ad map.
 *
 * Each map value is a comma-separated list of `provider:id` pairs
 * (e.g. `"vli:pw_49830,vli:pw_49831"` or `"epom:abc123"`), so the provider is
 * the token before the first `:` of every pair. Used as a fallback when the
 * offer omits the explicit `ad:banner-providers` / `ad:reward-providers` params.
 *
 * @param {Record<string, string>} adMap - Grouped ad map (e.g. `adConfig.banners` or `adConfig.rewards`).
 * @returns {string[]} Unique provider slugs in first-appearance order.
 */
const extractProviders = (adMap) => {
    const providers = new Set();

    for (const value of Object.values(adMap)) {
        for (const pair of value.split(',')) {
            const provider = pair.split(':')[0]?.trim();
            if (provider) providers.add(provider);
        }
    }

    return [...providers];
};

/**
 * Extracts ad settings from Sharpstar offer content into grouped banner/side/reward
 * maps, and derives the unique provider names actually used by those values.
 *
 * The `ad:banner-providers` and `ad:reward-providers` params are optional. When
 * present they drive dev mode (`=== 'dev'`) and the provider list as before.
 * When absent, dev mode is `false` and the providers are derived from the
 * banner/reward values via {@link extractProviders}.
 *
 * Empty banner/side values (`"empty"` / `"provider:empty"`) are omitted.
 * Empty reward values are kept so the game can still call that reward type and
 * receive a `REWARD_EMPTY` response.
 *
 * @param {Record<string, string>} inputObject - Raw offer content (e.g. `ad:banner:*`, `ad:side:*`, `ad:reward:*`).
 * @returns {{
 *   banners: Record<string, string>,
 *   sideBanners: Record<string, string>,
 *   rewards: Record<string, string>,
 *   bannerProviders: string[],
 *   rewardProviders: string[],
 * }}
 */
export const getAdConfig = (inputObject) => {
    const adConfig = {
        banners: {},
        rewards: {},
        sideBanners: {},
    };

    for (const [key, value] of Object.entries(inputObject)) {
        const trimmed = value.trim();

        const prefixEntry = PREFIX_MAP.find(([prefix]) => key.startsWith(prefix));
        if (!prefixEntry) continue;

        const [prefix, target] = prefixEntry;

        // Empty banner/side slots are omitted. Empty rewards stay in the map so
        // Unity `StartReward` can resolve them to REWARD_EMPTY instead of crashing.
        if (target !== 'rewards' && isEmptyAdValue(trimmed)) continue;

        adConfig[target][key.slice(prefix.length)] = trimmed;
    }

    // When the provider param is absent, dev mode is off and the providers
    // are derived from the actual banner/reward values in the offer.
    const rewardProvidersParam = inputObject['ad:reward-providers'];
    const bannerProvidersParam = inputObject['ad:banner-providers'];

    return {
        ...adConfig,
        rewardsAreDevMode: rewardProvidersParam === 'dev',
        bannersAreDevMode: bannerProvidersParam === 'dev',
        rewardProviders: rewardProvidersParam
            ? rewardProvidersParam.split(',')
            : extractProviders(adConfig.rewards),
        bannerProviders: bannerProvidersParam
            ? bannerProvidersParam.split(',')
            : extractProviders(adConfig.banners),
    };
};

export const sendRewardIsEmpty = (rewardType) => {
    Sentry.withScope((scope) => {
        scope.setTag('ads', 'lost-reward');
        scope.setLevel('warning');
        Sentry.captureMessage(
            `Ad Reward ${rewardType} id/VAST URL is missing in Sharpstar offer or lp default config`
        );
    });
};

export function getProviderSdkToLoad({ bannerProviders = [], rewardProviders = [] }) {
    const result = new Set();

    for (const p of bannerProviders) {
        if (p !== 'dev') result.add(p);
    }
    for (const p of rewardProviders) {
        if (p !== 'dev' && p !== 'epom') {
            result.add(p);
        }
    }

    return [...result];
}

/**
 * Collects unique reward placement IDs from provider:id pair config strings.
 *
 * @param {Record<string, string>} rewards
 * @param {string || "vl" } providerName
 * @returns {string[]}
 */
export function getRewardProviderIds(rewards = {}, providerName = 'vli') {
    const ids = new Set();

    if (!rewards || typeof rewards !== 'object') {
        return [];
    }

    for (const value of Object.values(rewards)) {
        if (typeof value !== 'string' || value.trim() === 'empty') continue;

        for (const pair of value.split(',')) {
            const [provider, id] = pair.split(':').map((part) => part?.trim());

            if (provider === providerName && id && provider !== 'empty' && id !== 'empty') {
                ids.add(id);
            }
        }
    }

    return [...ids];
}

export const parseAdIdPairs = (input) => {
    // Missing reward/banner config — callers use `pairs[userStatus] ?? pairs[0]`.
    if (!input || typeof input !== 'string') {
        return [['', 'empty']];
    }

    const parts = input.split(',').slice(0, 4);
    const firstPair = parts[0].split(':');

    return Array.from({ length: 4 }, (_, i) => {
        const pair = parts[i];

        return !pair || pair === 'empty' ? [...firstPair] : pair.split(':');
    });
};

/**
 * Displays a side video ad banner and tracks the analytics event (VLI ad provider ONLY).
 *
 * @param {HTMLElement} bannerElement - The banner element to display the video ad. (required)
 * @param {string} provider - Side ad video provider name. (required)
 * @param {Object} gameGA
 * @param {Object} ga
 */
export const showSideVideo = (bannerElement, provider, gameGA, ga) => {
    if (provider === 'dev' || !bannerElement) return;

    if (provider === 'vli') {
        const videoId = bannerElement.querySelector('div').getAttribute('data-ad-slot');

        (window.powerTag.Init = window.powerTag.Init || []).push(function () {
            powerAPITag.initPowerInstream(videoId);
        });

        const gaEventName = bannerElement.getAttribute('data-ga-id');

        checkAdContainerAndSendAnalytics(bannerElement, gaEventName, gameGA, ga, 'POWER');
    }
};
